import CtaArrow from "@/components/svg/CtaArrow";
import KeyPoint from "./KeyPoint";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const richTextBase = {
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
};

const sectionTitleComponents = {
  ...richTextBase,
  heading2: ({ children }) => <h2 className="h2-n lg:dk text-bleu">{children}</h2>,
  heading3: ({ children }) => <h3 className="h2-n lg:dk text-bleu">{children}</h3>,
};

const sectionTextComponents = {
  ...richTextBase,
  paragraph: ({ children }) => <p className="p-lg-n text-bleu">{children}</p>,
};

const cardTitleComponents = {
  ...richTextBase,
  heading3: ({ children }) => <h3 className="p-lg-n sbd text-bleu">{children}</h3>,
  heading4: ({ children }) => <h3 className="p-lg-n sbd text-bleu">{children}</h3>,
};

const cardTextComponents = {
  ...richTextBase,
  paragraph: ({ children }) => (
    <p className="p-base-n text-bleu not-last:mb-5">{children}</p>
  ),
};

/**
 * @param {{ sub_header?: string | null, title: import("@prismicio/client").RichTextField, text: import("@prismicio/client").RichTextField, textClassName?: string }} props
 */
const SectionIntro = ({ sub_header, title, text, textColor = "text-bleu" }) => {
  if (!isFilled.keyText(sub_header) && !isFilled.richText(title) && !isFilled.richText(text)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-1.5">
        {isFilled.keyText(sub_header) && (
          <p className="section-text-n text-rouge">{sub_header}</p>
        )}
        {isFilled.richText(title) && (
          <PrismicRichText field={title} components={sectionTitleComponents} />
        )}
      </div>
      {isFilled.richText(text) && (
        <PrismicRichText
          field={text}
          components={{
            ...sectionTextComponents,
            paragraph: ({ children }) => (
              <p className={`p-lg-n ${textColor}`}>{children}</p>
            ),
          }}
        />
      )}
    </div>
  );
};

/**
 * @param {{ item: import("@prismicio/client").Content.KeyPointsSliceInlinePrimaryCardsItem }} props
 */
const InlineCard = ({ item }) => {
  const hasImage = isFilled.image(item.picture);

  return (
    <li
      className={`grid grid-cols-1 content-start gap-y-4 rounded-[10px] bg-beige-50 px-[30px] py-10 lg:gap-x-[30px] lg:gap-y-[30px] ${hasImage ? "lg:grid-cols-[auto_1fr]" : ""}`}
    >
      {hasImage && (
        <div className="relative size-[100px] shrink-0 justify-self-center max-lg:mb-6 lg:self-center">
          <PrismicNextImage
            field={item.picture}
            fallbackAlt=""
            className="size-full object-contain"
            sizes="100px"
          />
        </div>
      )}
      {isFilled.richText(item.title) && (
        <div className="lg:self-center">
          <PrismicRichText field={item.title} components={cardTitleComponents} />
        </div>
      )}
      {isFilled.richText(item.text) && (
        <div className="lg:col-span-full">
          <PrismicRichText field={item.text} components={cardTextComponents} />
        </div>
      )}
    </li>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.KeyPointsSliceInline>} props
 */
const KeyPointsInline = ({ slice }) => {
  const { sub_header, title, text, cards } = slice.primary;
  const filledCards = Array.isArray(cards)
    ? cards.filter(
        (item) =>
          isFilled.richText(item.title) ||
          isFilled.richText(item.text) ||
          isFilled.image(item.picture),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-lg flex flex-col gap-[60px]">
        <SectionIntro sub_header={sub_header} title={title} text={text} />

        {filledCards.length > 0 && (
          <ul className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-[30px]">
            {filledCards.map((item, index) => (
              <InlineCard key={`card-${index}`} item={item} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.KeyPointsSliceStatic>} props
 */
const KeyPointsStatic = ({ slice }) => {
  const { picto, sub_header, title, text, cta, cards } = slice.primary;
  const filledCards = Array.isArray(cards)
    ? cards.filter(
        (item) => isFilled.richText(item.title) || isFilled.richText(item.text),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x py-15 lg:pb-[90px] lg:pt-20"
    >
      <div className="framing-lg grid grid-cols-1 gap-[60px] lg:grid-cols-[minmax(0,477px)_minmax(0,1fr)] lg:items-start lg:gap-x-[90px]">
        {/* Left part: stays in place while the cards scroll (desktop). On mobile the wrapper disappears and the cta moves after the cards. */}
        <div className="contents lg:col-start-1 lg:row-start-1 lg:sticky lg:top-[150px] lg:flex lg:flex-col lg:gap-10 lg:self-start">
          <div className="order-1 flex flex-col gap-10">
            {isFilled.image(picto) && (
              <PrismicNextImage
                field={picto}
                fallbackAlt=""
                className="h-[160px] w-auto max-w-full self-start object-contain object-left"
                sizes="163px"
              />
            )}
            <SectionIntro
              sub_header={sub_header}
              title={title}
              text={text}
              textColor="text-bleu-90"
            />
          </div>

          {isFilled.link(cta) && (
            <div className="order-3 lg:order-2">
              <PrismicNextLink
                prefetch={false}
                field={cta}
                className="cta cta-text w-fit bg-rouge text-beige-30"
              >
                {cta.text}
                <CtaArrow className="text-beige-30" />
              </PrismicNextLink>
            </div>
          )}
        </div>

        {filledCards.length > 0 && (
          <ul className="order-2 flex flex-col gap-[30px] lg:col-start-2 lg:row-start-1 lg:gap-7">
            {filledCards.map((item, index) => (
              <li
                key={`card-${index}`}
                className="flex flex-col gap-7 rounded-[10px] bg-beige-50 px-5 py-[30px] first:px-[30px] first:py-10 lg:px-[30px] lg:py-10"
              >
                {isFilled.richText(item.title) && (
                  <PrismicRichText field={item.title} components={cardTitleComponents} />
                )}
                {isFilled.richText(item.text) && (
                  <div>
                    <PrismicRichText field={item.text} components={cardTextComponents} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.KeyPointsSliceDefault>} props
 */
const KeyPointsDefault = ({ slice }) => {
  const { sub_header, title, text, cards, cta } = slice.primary;
  const filledCards = Array.isArray(cards)
    ? cards.filter(
        (item) =>
          isFilled.richText(item.title) ||
          isFilled.richText(item.text) ||
          isFilled.image(item.picture),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-sm flex flex-col items-center gap-10 lg:gap-15">
        <div className="flex w-full flex-col gap-15">
          <div className="flex flex-col gap-2.5 lg:gap-[50px]">
            <div className="flex flex-col gap-1.5">
              {isFilled.keyText(sub_header) && (
                <p className="section-text-n text-rouge">{sub_header}</p>
              )}
              {isFilled.richText(title) && (
                <PrismicRichText field={title} components={sectionTitleComponents} />
              )}
            </div>
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  ...richTextBase,
                  paragraph: ({ children }) => (
                    <p className="p-base-n text-bleu not-last:mb-5">{children}</p>
                  ),
                }}
              />
            )}
          </div>

          {filledCards.length > 0 && (
            <ul className="flex flex-col gap-[30px] lg:gap-10">
              {filledCards.map((item, index) => (
                <li key={`card-${index}`}>
                  <KeyPoint item={item} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {isFilled.link(cta) && (
          <PrismicNextLink
            prefetch={false}
            field={cta}
            className="cta cta-text w-fit bg-vert-50 text-beige-30"
          >
            {cta.text}
            <CtaArrow className="text-beige-30" />
          </PrismicNextLink>
        )}
      </div>
    </section>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.KeyPointsSliceNumbered>} props
 */
const KeyPointsNumbered = ({ slice }) => {
  const { sub_header, title, text, cards } = slice.primary;
  const filledPoints = Array.isArray(cards)
    ? cards.filter(
        (item) =>
          isFilled.richText(item.title) ||
          isFilled.richText(item.text) ||
          isFilled.image(item.picture),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 padding-x padding-y-md"
    >
      <div className="framing-sm flex flex-col gap-15">
        {(isFilled.keyText(sub_header) ||
          isFilled.richText(title) ||
          isFilled.richText(text)) && (
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-1.5">
              {isFilled.keyText(sub_header) && (
                <p className="section-text-n text-rouge">{sub_header}</p>
              )}
              {isFilled.richText(title) && (
                <PrismicRichText field={title} components={sectionTitleComponents} />
              )}
            </div>
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  ...richTextBase,
                  paragraph: ({ children }) => (
                    <p className="p-base-n text-bleu not-last:mb-5">{children}</p>
                  ),
                }}
              />
            )}
          </div>
        )}

        {filledPoints.length > 0 && (
          <ol className="flex flex-col gap-[30px]">
            {filledPoints.map((item, index) => (
              <li
                key={`point-${index}`}
                className="relative lg:grid lg:grid-cols-[51px_1fr] lg:items-center lg:gap-x-[60px]"
              >
                <span
                  aria-hidden
                  className="hidden text-center font-body text-[46px] font-thin uppercase leading-[normal] tracking-[.5px] text-bleu lg:block"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                {index < filledPoints.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute left-[25px] top-[calc(100%+15px-43px)] hidden h-[86px] w-px bg-bleu-50 lg:block"
                  />
                )}
                <KeyPoint item={item} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.KeyPointsSlice} KeyPointsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<KeyPointsSlice>} KeyPointsProps
 * @type {import("react").FC<KeyPointsProps>}
 */
const KeyPoints = ({ slice }) => {
  switch (slice.variation) {
    case "default":
      return <KeyPointsDefault slice={slice} />;
    case "numbered":
      return <KeyPointsNumbered slice={slice} />;
    case "static":
      return <KeyPointsStatic slice={slice} />;
    case "inline":
    default:
      return <KeyPointsInline slice={slice} />;
  }
};

export default KeyPoints;
